import { showUserAlert } from "../utils/userErrorMessage";
import React, { useState, useEffect, useRef } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { supabase } from "../supabase/client";
import { useNavigate } from "react-router-dom";
import Header from "../Component/Header";
import { syncDriverProfileFromCar } from "../utils/driverCarSync";

export default function CarAddForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    car_number: "",
    car_name: "",
    driver: "",
    driver_id: "",
    driver_email: "",
    phone: "",
    year: "",
    seat: "",
    seat_detail: "",
    karaoke_machine: false,
    electronic_display: false,
  });
  const [saving, setSaving] = useState(false);

  // 🔽 기사 자동완성 관련 상태
  const [allDrivers, setAllDrivers] = useState([]);     // 전체 후보 (미배정 기사)
  const [filtered, setFiltered] = useState([]);         // 입력값으로 필터된 후보
  const [showList, setShowList] = useState(false);      // 드롭다운 표시 여부
  const listRef = useRef(null);

  const handleChange = (field, value) => {
    const nextForm = { ...form, [field]: value };

    // 기사명 입력 변화에 따라 필터링
    if (field === "driver") {
      nextForm.driver_id = "";
      nextForm.driver_email = "";
      nextForm.phone = "";
      const q = (value || "").toLowerCase();
      const next = allDrivers.filter(d =>
        (d.driver_name || "").toLowerCase().includes(q)
      );
      setFiltered(next);
      setShowList(true);
    }
    setForm(nextForm);
  };

  // 🔽 기사 후보 로드: 회사 기준, role='driver', car_number 비어있는 기사만
  const loadUnassignedDrivers = async () => {
    const company = localStorage.getItem("email");
    if (!company) return;

    // company+role로 1차 조회 후, car_number 비거나 null인 사람만 JS에서 필터
    const { data, error } = await supabase
      .from("profiles")
      .select("id, driver_name, email, phone, car_number, role, company")
      .eq("company", company)
      .eq("role", "driver");
    if (error) {
      console.error("profiles 조회 실패:", error.message);
      setAllDrivers([]);
      setFiltered([]);
      return;
    }
    const unassigned = (data || []).filter(d => !d.car_number || `${d.car_number}`.trim() === "");
    setAllDrivers(unassigned);
    setFiltered(unassigned);
  };

  // 🔽 기사 입력칸 포커스 시 목록 준비
  const handleDriverFocus = async () => {
    await loadUnassignedDrivers();
    setShowList(true);
  };

  // 🔽 기사 선택 시 폼 채움 + 닫기
  const selectDriver = (d) => {
    setForm((prev) => ({
      ...prev,
      driver: d.driver_name || "",
      driver_id: d.id || "",
      driver_email: d.email || "",
      phone: d.phone || "",
    }));
    setShowList(false);
  };

  const resolveDriverProfileForSync = async (company) => {
    const driverName = (form.driver || "").trim();
    if (!driverName) return null;

    if (form.driver_id) {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, driver_name, email, phone")
        .eq("id", form.driver_id)
        .maybeSingle();
      if (error) throw error;
      if (data) return data;
    }

    const email = (form.driver_email || "").trim();
    if (email) {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, driver_name, email, phone")
        .eq("company", company)
        .eq("role", "driver")
        .eq("email", email)
        .maybeSingle();
      if (error) throw error;
      if (data) return data;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("id, driver_name, email, phone")
      .eq("company", company)
      .eq("role", "driver")
      .eq("driver_name", driverName);
    if (error) throw error;
    if ((data || []).length === 1) return data[0];
    if ((data || []).length > 1) {
      throw new Error(`${driverName} 기사명이 여러 명입니다. 목록에서 정확한 기사를 선택해 주세요.`);
    }
    return null;
  };

  // 🔽 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!listRef.current) return;
      if (!listRef.current.contains(e.target)) {
        setShowList(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleSubmit = async () => {
    if (saving) return;
    const company = localStorage.getItem("email");
    if (!form.car_number) return showUserAlert("차량번호를 입력하세요.");

    setSaving(true);
    try {
      const car_number_only = (form.car_number || "").slice(-4);
      const company_name = localStorage.getItem("companyName") || company;
      const resolvedDriver = await resolveDriverProfileForSync(company);
      const driverName = resolvedDriver?.driver_name || form.driver || null;
      const driverEmail = resolvedDriver?.email || form.driver_email || null;
      const driverPhone = form.phone || resolvedDriver?.phone || null;

      // 1) cars 등록
      const payload = {
        car_number: form.car_number,
        car_name: form.car_name || null,
        driver: driverName,
        driver_email: driverEmail,
        phone: driverPhone,
        year: form.year || null,
        seat: form.seat || null,
        seat_detail: form.seat_detail || null,
        karaoke_machine: !!form.karaoke_machine,
        electronic_display: !!form.electronic_display,
        car_number_only,
        company,
        company_name,
      };
      const { data: insertedCar, error: carErr } = await supabase
        .from("cars")
        .insert([payload])
        .select("id, car_number, car_number_only, driver, driver_email, phone")
        .single();
      if (carErr) throw carErr;

      // 2) 연결된 profiles(role='driver')의 car_number 동기화
      await syncDriverProfileFromCar(supabase, {
        company,
        car: insertedCar || payload,
        previousCar: null,
      });

      showUserAlert("차량이 등록되었습니다.");
      navigate("/car-info");
    } catch (e) {
      showUserAlert("등록 실패: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Header />
      <div className="p-4">
        <h2 className="text-lg font-semibold">차량 추가 등록</h2>
        <Card className="p-4">
          <CardContent className="bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 차량번호 */}
              <div>
                <label className="block font-semibold mb-1">차량번호</label>
                <Input
                  value={form.car_number}
                  onChange={(e) => handleChange("car_number", e.target.value)}
                  disabled={saving}
                />
              </div>

              {/* 차명 */}
              <div>
                <label className="block font-semibold mb-1">차명</label>
                <Input
                  value={form.car_name || ""}
                  onChange={(e) => handleChange("car_name", e.target.value)}
                  disabled={saving}
                />
              </div>

              {/* 기사명 + 자동완성 */}
              <div className="relative" ref={listRef}>
                <label className="block font-semibold mb-1">기사명</label>
                <Input
                  value={form.driver || ""}
                  onChange={(e) => handleChange("driver", e.target.value)}
                  onFocus={handleDriverFocus}
                  placeholder="클릭하면 미배정 기사 목록이 열립니다"
                  disabled={saving}
                />
                {showList && filtered.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto border bg-white rounded shadow">
                    {filtered.map((d) => (
                      <li
                        key={d.id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex flex-col"
                        onClick={() => selectDriver(d)}
                      >
                        <span className="font-medium">{d.driver_name}</span>
                        <span className="text-xs text-gray-500">
                          {d.email || "이메일 없음"} · {d.phone || "연락처 없음"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* 기사 이메일 */}
              <div>
                <label className="block font-semibold mb-1">기사 이메일</label>
                <Input
                  value={form.driver_email || ""}
                  onChange={(e) => handleChange("driver_email", e.target.value)}
                  disabled={saving}
                />
              </div>

              {/* 핸드폰 */}
              <div>
                <label className="block font-semibold mb-1">핸드폰</label>
                <Input
                  value={form.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  disabled={saving}
                />
              </div>

              {/* 년식 */}
              <div>
                <label className="block font-semibold mb-1">년식</label>
                <Input
                  value={form.year || ""}
                  onChange={(e) => handleChange("year", e.target.value)}
                  disabled={saving}
                />
              </div>

              {/* 승차인원 */}
              <div>
                <label className="block font-semibold mb-1">승차인원</label>
                <select
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  value={form.seat || ""}
                  onChange={(e) => handleChange("seat", e.target.value)}
                  disabled={saving}
                >
                  <option value="">선택하세요</option>
                  <option value="대형">대형</option>
                  <option value="중형">중형</option>
                  <option value="우등">우등</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-1">
                  좌석 상세 <span className="text-xs text-gray-500">(예: 45인승, 28인승 / 비워도 됨)</span>
                </label>
                <Input
                  value={form.seat_detail || ""}
                  onChange={(e) => handleChange("seat_detail", e.target.value)}
                  disabled={saving}
                  placeholder="예) 45인승 / 28인승 / 리무진 21인승 등"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="karaoke_machine"
                  type="checkbox"
                  checked={!!form.karaoke_machine}
                  onChange={(e) => handleChange("karaoke_machine", e.target.checked)}
                  disabled={saving}
                />
                <label htmlFor="karaoke_machine" className="font-semibold">
                  노래방기기
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="electronic_display"
                  type="checkbox"
                  checked={!!form.electronic_display}
                  onChange={(e) => handleChange("electronic_display", e.target.checked)}
                  disabled={saving}
                />
                <label htmlFor="electronic_display" className="font-semibold">
                  전광판
                </label>
              </div>

              {/* 버튼 */}
              <div className="md:col-span-2 flex gap-4">
                <Button onClick={handleSubmit} disabled={saving}>
                  {saving ? "등록중..." : "등록"}
                </Button>
                <Button variant="outline" onClick={() => navigate("/car-info")} disabled={saving}>
                  돌아가기
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
